/** Match Engine 2.5 helpers. Cards/suspension treat missing as 0. Captain, formation styles and practice-boost UI stay visible with defaults until the API sends real values. */

/** Empty captain block so the picker can be designed without the strategy payload field. */
export const DEFAULT_CAPTAIN = {
  player: null,
  auto: false,
  change: { allowed: true, free: true, reason: 'first_pick' },
  seasonChangesLeft: 1,
  preview: { status: 'none', backing: null },
};

/** Formation style map used until `/team/strategy` includes `formationStyles`. */
export const DEFAULT_FORMATION_STYLES = {
  byFormation: {
    '4-4-2': 'Direct',
    '4-3-3': 'Wide',
    '4-2-3-1': 'Wide',
    '4-5-1': 'Central',
    '3-5-2': 'Central',
    '4-4-1-1': 'Direct',
  },
  beats: {
    Wide: 'Central',
    Central: 'Direct',
    Direct: 'Wide',
  },
};

export const LINEUP_FORBIDDEN_MESSAGE =
  "This lineup isn't allowed. Check for suspended, injured or sent-off players.";

export const playerYellowCards = (player) => {
  if (!player || player.yellow_cards == null) return 0;
  const n = Number(player.yellow_cards);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
};

export const playerSuspension = (player) => {
  if (!player || player.suspension == null) return 0;
  const n = Number(player.suspension);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
};

export const isInjured = (player) =>
  player?.form_range === -1 || player?.formId === -1 || player?.form === 'Injured';

export const isSuspended = (player) => playerSuspension(player) > 0;

/** Blocked from entering the XI (new pick). Injured starters already in XI may stay. */
export const cannotEnterXi = (player) => isInjured(player) || isSuspended(player);

export const SUSPENDED_HINT = 'Suspended: misses the next league or cup match.';

export const ownClubId = (matchRunning, userTeamId) => {
  if (!matchRunning || !userTeamId) return null;
  const homeId =
    matchRunning.home_team?._id ||
    matchRunning.home_team?.id ||
    matchRunning.home_team_id;
  const awayId =
    matchRunning.away_team?._id ||
    matchRunning.away_team?.id ||
    matchRunning.away_team_id;
  if (String(homeId) === String(userTeamId)) return 'home';
  if (String(awayId) === String(userTeamId)) return 'away';
  return null;
};

export const redCardEntries = (matchRunning, side) => {
  if (!matchRunning || !side) return [];
  const stats = matchRunning[`${side}_team_statistics`];
  const cards = stats?.red_cards;
  return Array.isArray(cards) ? cards : [];
};

export const yellowCardEntries = (matchRunning, side) => {
  if (!matchRunning || !side) return [];
  const stats = matchRunning[`${side}_team_statistics`];
  const cards = stats?.yellow_cards;
  return Array.isArray(cards) ? cards : [];
};

const firstEventArray = (...candidates) =>
  candidates.find((value) => Array.isArray(value)) || null;

const isSubIncidentType = (entry) => {
  const raw =
    entry?.type ??
    entry?.kind ??
    entry?.event_type ??
    entry?.eventType ??
    entry?.incidentType ??
    entry?.incident_type ??
    null;
  if (raw == null) return false;
  const label = String(raw).toLowerCase().replace(/[\s-]+/g, '_');
  return (
    label === 'substitution' ||
    label === 'substitute' ||
    label === 'sub' ||
    label === 'substitution_in' ||
    label === 'substitution_out'
  );
};

/** True when an object looks like a timed sub event, not a bench roster row. */
export const isSubstitutionEvent = (entry) => {
  if (!entry || typeof entry !== 'object') return false;
  if (
    entry.player_in ||
    entry.player_out ||
    entry.in_player ||
    entry.out_player ||
    entry.playerIn ||
    entry.playerOut ||
    entry.player_on ||
    entry.player_off ||
    entry.on_player ||
    entry.off_player ||
    entry.substitute_in ||
    entry.substitute_out ||
    entry.substituted_in ||
    entry.substituted_out
  ) {
    return true;
  }
  if (isSubIncidentType(entry)) return true;
  if (
    eventMinute(entry) != null &&
    (entry.in || entry.out || entry.replaced || entry.replacement || entry.substitute)
  ) {
    return true;
  }
  return false;
};

const pickSubPlayer = (entry, keys) => {
  if (!entry || typeof entry !== 'object') return null;
  for (let i = 0; i < keys.length; i += 1) {
    const value = entry[keys[i]];
    if (value == null || value === '') continue;
    if (typeof value === 'object' || typeof value === 'string') return value;
  }
  return null;
};

export const substitutionInPlayer = (entry) =>
  pickSubPlayer(entry, [
    'player_in',
    'in_player',
    'playerIn',
    'player_on',
    'on_player',
    'substitute_in',
    'substituted_in',
    'replacement',
    'substitute',
    'in',
  ]);

export const substitutionOutPlayer = (entry) =>
  pickSubPlayer(entry, [
    'player_out',
    'out_player',
    'playerOut',
    'player_off',
    'off_player',
    'substitute_out',
    'substituted_out',
    'replaced',
    'out',
  ]) ||
  // Some feeds only nest the player leaving under `player`.
  (isSubIncidentType(entry) ? entry.player || null : null);

/** Timed substitutions for one side (not bench roster lists). */
export const substitutionEntries = (matchRunning, side) => {
  if (!matchRunning || !side) return [];
  const stats = matchRunning[`${side}_team_statistics`] || {};
  const team = matchRunning[`${side}_team`] || {};
  const raw = firstEventArray(
    stats.substitutions,
    stats.substitution_events,
    stats.subs,
    matchRunning[`${side}_substitutions`],
    matchRunning[`${side}Substitutions`],
    team.substitutions,
    team.substitution_events
  );
  let list = Array.isArray(raw) ? raw.filter(isSubstitutionEvent) : [];

  if (!list.length) {
    const incidents = firstEventArray(
      matchRunning.events,
      matchRunning.timeline,
      matchRunning.incidents,
      matchRunning.match_events,
      matchRunning.matchEvents
    );
    if (Array.isArray(incidents)) {
      list = incidents.filter((entry) => {
        if (!isSubstitutionEvent(entry)) return false;
        const entrySide =
          entry.side ||
          entry.team_side ||
          entry.teamSide ||
          (entry.team === 'home' || entry.team === 'away' ? entry.team : null);
        if (entrySide) return String(entrySide).toLowerCase() === side;
        const teamId =
          entry.team_id ||
          entry.teamId ||
          entry.club_id ||
          entry.clubId ||
          entry.team?._id ||
          entry.team?.id ||
          null;
        if (teamId == null) return false;
        const sideId =
          team._id ||
          team.id ||
          matchRunning[`${side}_team_id`] ||
          null;
        return sideId != null && String(teamId) === String(sideId);
      });
    }
  }

  return list.map((entry) => ({
    ...entry,
    minute: eventMinute(entry),
    player_in: substitutionInPlayer(entry),
    player_out: substitutionOutPlayer(entry),
  }));
};

export const substitutionLabel = (entry) => {
  const inName = eventPlayerName(entry?.player_in || substitutionInPlayer(entry));
  const outName = eventPlayerName(
    entry?.player_out || substitutionOutPlayer(entry)
  );
  if (inName && outName) return `${inName} for ${outName}`;
  if (inName) return inName;
  if (outName) return outName;
  return null;
};

/** Parse a minute token: 23, "23", "23'", "23 min". */
export const parseMinuteToken = (raw) => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const n = Math.round(raw);
    return n >= 0 && n <= 130 ? n : null;
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    const matched = trimmed.match(/^(\d{1,3})\s*'?/);
    if (matched) return Number(matched[1]);
    const n = Number(trimmed);
    return Number.isFinite(n) && n >= 0 && n <= 130 ? Math.round(n) : null;
  }
  if (typeof raw === 'object') {
    return parseMinuteToken(
      raw.minute ??
        raw.match_minute ??
        raw.matchMinute ??
        raw.elapsed ??
        raw.clock ??
        null
    );
  }
  return null;
};

/**
 * Best-effort minute from a goal/card entry (API field names vary).
 * When several fields disagree, prefer a plausible match-clock value
 * (Discord "minute 23" vs a stale/segment field like 3).
 */
export const eventMinute = (entry) => {
  if (entry == null) return null;
  if (typeof entry !== 'object') return parseMinuteToken(entry);

  const candidates = [];
  const add = (value) => {
    const n = parseMinuteToken(value);
    if (n != null) candidates.push(n);
  };

  add(entry.match_minute);
  add(entry.matchMinute);
  add(entry.minute_of_match);
  add(entry.goal_minute);
  add(entry.scored_minute);
  add(entry.clock);
  add(entry.elapsed);
  add(entry.Minute);
  add(entry.minutes);
  add(entry.time);
  add(entry.minute);
  add(entry.min);
  add(entry.m);
  add(entry.player?.match_minute);
  add(entry.player?.minute);

  if (!candidates.length) return null;
  if (candidates.length === 1) return candidates[0];
  const plausible = candidates.filter((n) => n >= 1 && n <= 120);
  if (plausible.length) return Math.max(...plausible);
  return candidates[0];
};

/** Scorer / carded player display name from varied API shapes. */
export const eventPlayerName = (entry) => {
  if (entry == null) return null;
  if (typeof entry === 'string') return entry.trim() || null;
  if (typeof entry !== 'object') return null;
  const person = entry.player && typeof entry.player === 'object' ? entry.player : entry;
  if (typeof person === 'string') return person.trim() || null;
  const direct =
    person.name ||
    entry.name ||
    entry.player_name ||
    person.player_name ||
    entry.scorer ||
    person.scorer ||
    null;
  if (direct && String(direct).trim()) return String(direct).trim();
  const first = person.first_name || person.firstName || '';
  const last = person.last_name || person.lastName || '';
  const full = `${first} ${last}`.trim();
  return full || null;
};

export const goalEntries = (matchRunning, side) => {
  if (!matchRunning || !side) return [];
  const stats = matchRunning[`${side}_team_statistics`];
  const goals = stats?.goals;
  if (!Array.isArray(goals)) return [];
  return goals.map((goal) => {
    if (goal == null || typeof goal !== 'object') {
      return { minute: eventMinute(goal), player: null };
    }
    return {
      ...goal,
      minute: eventMinute(goal),
      player: goal.player || null,
    };
  });
};

/** Sorted timeline events for one side: goals + yellows + reds + subs. */
export const sideMatchEvents = (matchRunning, side) => {
  const events = [];
  goalEntries(matchRunning, side).forEach((g, i) => {
    events.push({
      kind: 'goal',
      minute: g.minute,
      playerName: eventPlayerName(g),
      playerId: g.player?._id || g.player?.id || g._id || null,
      side,
      key: `g-${side}-${i}`,
    });
  });
  yellowCardEntries(matchRunning, side).forEach((c, i) => {
    events.push({
      kind: 'yellow',
      minute: eventMinute(c),
      playerName: eventPlayerName(c),
      playerId: c.player?._id || c.player?.id || c._id || null,
      side,
      key: `y-${side}-${i}`,
    });
  });
  redCardEntries(matchRunning, side).forEach((c, i) => {
    events.push({
      kind: 'red',
      minute: eventMinute(c),
      secondYellow: !!(c.second_yellow || c.secondYellow),
      playerName: eventPlayerName(c),
      playerId: c.player?._id || c.player?.id || c._id || null,
      side,
      key: `r-${side}-${i}`,
    });
  });
  substitutionEntries(matchRunning, side).forEach((s, i) => {
    const inPlayer = s.player_in;
    const outPlayer = s.player_out;
    events.push({
      kind: 'sub',
      minute: s.minute,
      playerName: substitutionLabel(s),
      playerInName: eventPlayerName(inPlayer),
      playerOutName: eventPlayerName(outPlayer),
      playerId:
        (inPlayer && typeof inPlayer === 'object'
          ? inPlayer._id || inPlayer.id
          : null) || null,
      playerOutId:
        (outPlayer && typeof outPlayer === 'object'
          ? outPlayer._id || outPlayer.id
          : null) || null,
      side,
      key: `s-${side}-${i}`,
    });
  });
  return events.sort((a, b) => {
    const am = a.minute == null ? 999 : Number(a.minute);
    const bm = b.minute == null ? 999 : Number(b.minute);
    return am - bm;
  });
};

const goalPlayerKey = (event) => {
  if (event?.playerId != null && event.playerId !== '') {
    return `id:${event.playerId}`;
  }
  if (event?.playerName) return `name:${event.playerName}`;
  return null;
};

/**
 * Collapse multiple goals by the same player into one mark
 * (⚽ 23', 28' Name) so the live bar stays compact.
 */
export const groupSideMatchEvents = (events) => {
  if (!Array.isArray(events) || !events.length) return [];
  const groups = [];
  const goalGroupAt = new Map();

  events.forEach((event) => {
    const minutes =
      event.minute != null && Number.isFinite(Number(event.minute))
        ? [Number(event.minute)]
        : [];

    if (event.kind === 'goal') {
      const pid = goalPlayerKey(event);
      if (pid && goalGroupAt.has(pid)) {
        const group = groups[goalGroupAt.get(pid)];
        minutes.forEach((m) => group.minutes.push(m));
        group.minutes.sort((a, b) => a - b);
        group.minute = group.minutes[0];
        group.key = `${group.key}+${event.key}`;
        return;
      }
      const group = { ...event, minutes };
      if (pid) goalGroupAt.set(pid, groups.length);
      groups.push(group);
      return;
    }

    groups.push({ ...event, minutes });
  });

  return groups.sort((a, b) => {
    const am = a.minutes?.[0] ?? a.minute ?? 999;
    const bm = b.minutes?.[0] ?? b.minute ?? 999;
    return Number(am) - Number(bm);
  });
};

/** Full match timeline (home + away), sorted by minute. */
export const matchTimeline = (match) => {
  const events = [
    ...sideMatchEvents(match, 'home'),
    ...sideMatchEvents(match, 'away'),
  ];
  return events.sort((a, b) => {
    const am = a.minute == null ? 999 : Number(a.minute);
    const bm = b.minute == null ? 999 : Number(b.minute);
    if (am !== bm) return am - bm;
    if (a.side === b.side) return 0;
    return a.side === 'home' ? -1 : 1;
  });
};

export const sentOffPlayerIds = (matchRunning, userTeamId) => {
  const side = ownClubId(matchRunning, userTeamId);
  if (!side) return new Set();
  const ids = new Set();
  redCardEntries(matchRunning, side).forEach((entry) => {
    const id = entry?.player?._id || entry?.player?.id;
    if (id) ids.add(String(id));
  });
  return ids;
};

export const isSentOff = (playerId, matchRunning, userTeamId) => {
  if (!playerId) return false;
  return sentOffPlayerIds(matchRunning, userTeamId).has(String(playerId));
};

/**
 * Client mirror of backend lineup rules for ME 2.5.
 * Returns { ok, reason } where reason is 'incomplete' | 'suspended' | 'injured' | 'sent_off'.
 */
export const validateLineup = (xiPlayers, { matchRunning, userTeamId } = {}) => {
  const xi = Array.isArray(xiPlayers) ? xiPlayers.filter(Boolean) : [];
  if (xi.length !== 11) return { ok: false, reason: 'incomplete' };

  const sentOff = sentOffPlayerIds(matchRunning, userTeamId);
  const xiIds = new Set(xi.map((p) => String(p._id)));

  for (const id of sentOff) {
    if (!xiIds.has(id)) return { ok: false, reason: 'sent_off' };
  }

  for (const player of xi) {
    if (isSuspended(player)) return { ok: false, reason: 'suspended' };
    // Backend allows injured players already in the saved XI; we only block
    // when the client would newly introduce them — treat any injured in draft
    // the same as before ME 2.5 for save (existing UX), unless already sent-off.
    if (isInjured(player) && !sentOff.has(String(player._id))) {
      // Keep prior web behaviour: injured in XI blocks save. Optional to match
      // backend later; plan notes this difference.
      return { ok: false, reason: 'injured' };
    }
  }

  return { ok: true };
};

export const describeLineupProblem = (reason) => {
  if (reason === 'incomplete') return 'You need eleven players on the pitch!';
  return LINEUP_FORBIDDEN_MESSAGE;
};

export const CAPTAIN_PREVIEW_COPY = {
  none: 'No captain',
  vacated: 'Your captain has left: pick a new one, free',
  excused: 'Captain unavailable (injured or suspended)',
  active: {
    high: 'Captain active, strong backing',
    mid: 'Captain active, solid backing',
    low: 'Captain active, little backing',
  },
  benched: "Captain benched: the side misses his leadership",
};

export const captainPreviewLabel = (preview) => {
  if (!preview || !preview.status) return null;
  const { status, backing } = preview;
  if (status === 'active') {
    return CAPTAIN_PREVIEW_COPY.active[backing] || CAPTAIN_PREVIEW_COPY.active.mid;
  }
  return CAPTAIN_PREVIEW_COPY[status] || null;
};

export const captainChangeLabel = (change, seasonChangesLeft) => {
  if (!change) return null;
  if (!change.allowed) {
    if (change.reason === 'match_running') return "You can't change captain during a match.";
    if (change.reason === 'no_changes_left') return "You've used this season's captain change.";
    return 'Captain changes are locked.';
  }
  if (change.free) {
    if (change.reason === 'first_pick') return 'First pick is free.';
    if (change.reason === 'vacated') return 'Free re-pick — previous captain left.';
    if (change.reason === 'long_injury') return 'Free re-pick — long injury.';
    if (change.reason === 'new_manager') return 'Free re-pick — new manager.';
    if (change.reason === 'off_season') return 'Free to change in the off-season.';
    return 'Free to change.';
  }
  if (change.reason === 'season_change') {
    const left = seasonChangesLeft != null ? seasonChangesLeft : 1;
    return left === 1
      ? 'Uses your one captain change this season.'
      : `Season changes left: ${left}.`;
  }
  return 'Captain change available.';
};

export const setCaptainErrorMessage = (status, body) => {
  if (status === 200) return null;
  if (status === 403) {
    const reason = body?.reason;
    if (reason === 'academy') return "Academy players can't be captain.";
    if (reason === 'keeper') return "Keepers can't be captain.";
    return "That player isn't at your club.";
  }
  if (status === 409) {
    const reason = body?.reason;
    if (reason === 'match_running') return "You can't change captain during a match.";
    if (reason === 'no_changes_left') return "You've used this season's captain change.";
  }
  return 'Could not set captain. Please try again.';
};

export const isEligibleCaptain = (player) => {
  if (!player) return false;
  if (player.fav_position === 'GK' || player.current_position === 'GK') return false;
  if (player.academy || player.in_academy || player.status === 'academy') return false;
  if (player.loaned_out || player.on_loan || player.loan_out) return false;
  return true;
};

/** Merge API / partial payloads with design defaults so labels never go blank. */
export const normalizeFormationStyles = (raw) => {
  if (!raw || typeof raw !== 'object') return DEFAULT_FORMATION_STYLES;
  const beats =
    raw.beats && typeof raw.beats === 'object'
      ? { ...DEFAULT_FORMATION_STYLES.beats, ...raw.beats }
      : DEFAULT_FORMATION_STYLES.beats;

  if (raw.byFormation && typeof raw.byFormation === 'object') {
    return {
      byFormation: {
        ...DEFAULT_FORMATION_STYLES.byFormation,
        ...raw.byFormation,
      },
      beats,
    };
  }

  // Flat map: { '4-3-3': 'Wide', beats? }
  const flat = Object.fromEntries(
    Object.entries(raw).filter(
      ([key, value]) =>
        key !== 'beats' &&
        key !== 'byFormation' &&
        typeof value === 'string'
    )
  );
  if (Object.keys(flat).length) {
    return {
      byFormation: { ...DEFAULT_FORMATION_STYLES.byFormation, ...flat },
      beats,
    };
  }

  return { byFormation: { ...DEFAULT_FORMATION_STYLES.byFormation }, beats };
};

export const formationStyleOf = (formationStyles, formationCode) => {
  const styles = normalizeFormationStyles(formationStyles);
  if (formationCode == null) return null;
  const key = String(formationCode);
  return styles.byFormation[key] || styles.byFormation[formationCode] || null;
};

export const matchupLabel = (formationStyles, myStyle, theirStyle) => {
  if (!myStyle || !theirStyle || !formationStyles?.beats) return null;
  const beats = formationStyles.beats;
  if (beats[myStyle] === theirStyle) return 'Your style holds the counter';
  if (beats[theirStyle] === myStyle) return 'Countered';
  return 'Even';
};

export const hasFavStyleField = (playerOrList) => {
  if (!playerOrList) return false;
  if (Array.isArray(playerOrList)) {
    return playerOrList.some((p) => p && Object.prototype.hasOwnProperty.call(p, 'fav_style'));
  }
  return Object.prototype.hasOwnProperty.call(playerOrList, 'fav_style');
};

export const FAV_STYLE_OPTIONS = [
  { value: 'Wide', label: 'Wide' },
  { value: 'Central', label: 'Central' },
  { value: 'Direct', label: 'Direct' },
];

// API tactics.effort codes 0 / 1 / 2 — labels only; codes unchanged since Match Engine 2.5.
export const EFFORT_LABELS = ['Low', 'Medium', 'Extreme'];

export const EFFORT_EXPLAINERS = {
  Low: 'Players recover fitness while they play; the side plays with less intensity.',
  Medium: 'The sustainable default; fitness holds steady for most players.',
  Extreme:
    'Maximum intensity for a final push. Fitness drains fast, and injuries and bookings become much more likely.',
};

export const EFFORT_FOOTNOTE =
  'Keepers tire less than outfield players. Players well before or past their peak tire faster than players at their peak.';

export const formatPracticeBoostUntil = (iso, formatFn) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    if (typeof formatFn === 'function') return formatFn(d);
    return d.toLocaleString(undefined, {
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return null;
  }
};

export const REPORT_CARD_EVENTS = new Set([
  'yellow_card',
  'second_yellow',
  'red_card',
]);

export const isCardReportEvent = (type) => REPORT_CARD_EVENTS.has(type);
